import React, { useState, useEffect } from 'react';
import { api } from '../../lib/api';

const CustomerList = () => {
    const [users, setUsers] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [searching, setSearching] = useState(false);

    useEffect(() => {
        fetchUsers();
    }, []);

    const fetchUsers = async () => {
        setLoading(true);
        try {
            // Fetch count only initially for performance
            const response = await api.get('/api/admin/customers?count_only=true');
            if (response.ok) {
                const data = await response.json();
                setUsers([]); // Initial load has no users
                setTotalCount(data.total_count || 0);
            } else {
                setError("Failed to load customers");
            }
        } catch (err) {
            setError("Failed to load customers");
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = async (e) => {
        e.preventDefault();
        if (!searchQuery.trim()) return;

        setSearching(true);
        try {
            const response = await api.get(`/api/admin/customers/search?email=${encodeURIComponent(searchQuery)}`);
            if (response.ok) {
                const data = await response.json();
                setUsers(data || []);
                // If search found something, total count is 1, else 0. 
                // But let's keep the global count if possible? 
                // Actually, the requirements say "replace the displayedUsers table with the single result".
                // We'll just update the list. The "Total Registered Customers" widget should ideally persist,
                // but for MVP it's okay if it reflects current view or stays static.
                // Let's NOT update totalCount here so the widget stays accurate to the platform total.
            } else {
                // If 404 or other, clear list
                setUsers([]);
            }
        } catch (err) {
            setUsers([]);
        } finally {
            setSearching(false);
        }
    };

    const handleClearSearch = () => {
        setSearchQuery('');
        fetchUsers();
    };

    const getInitials = (name) => {
        return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    };

    const getAvatarColor = (name) => {
        const colors = [
            'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
            'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)',
            'linear-gradient(135deg, #ec4899 0%, #be185d 100%)',
            'linear-gradient(135deg, #10b981 0%, #047857 100%)',
            'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
            'linear-gradient(135deg, #ef4444 0%, #b91c1c 100%)',
            'linear-gradient(135deg, #6366f1 0%, #4338ca 100%)',
            'linear-gradient(135deg, #14b8a6 0%, #0f766e 100%)',
        ];
        const index = name.charCodeAt(0) % colors.length;
        return colors[index];
    };

    if (loading && !searching && users.length === 0) {
        return (
            <div className="loading-spinner">
                <div className="spinner"></div>
                <p className="loading-text">Loading customers...</p>
            </div>
        );
    }

    return (
        <div className="animate-fadeIn">
            <div className="team-header">
                <div>
                    <h1>Customer Directory</h1>
                    <p>View and search all registered customers.</p>
                </div>
            </div>

            {/* Top Metric Widget */}
            <div className="dashboard-stats" style={{ marginBottom: '2rem', gridTemplateColumns: '1fr' }}>
                <div className="stat-card">
                    <div className="stat-icon">👥</div>
                    <div className="stat-number">{totalCount}</div>
                    <div className="stat-label">Total Registered Customers</div>
                </div>
            </div>

            {error && <div style={{ padding: '1rem', background: '#fee', color: '#b91c1c', border: '1px solid #fca5a5', borderRadius: '0.75rem', marginBottom: '1.5rem' }}>{error}</div>}

            <form onSubmit={handleSearch} className="search-box">
                <input
                    type="text"
                    placeholder="Search by exact email address..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="search-input"
                />
                <button type="submit" className="search-icon-btn" disabled={searching}>
                    {searching ? '...' : '🔍'}
                </button>
                {searchQuery && (
                    <button type="button" onClick={handleClearSearch} className="search-clear">✕</button>
                )}
            </form>

            <div className="team-table-container">
                <table className="team-table">
                    <thead>
                        <tr>
                            <th>Customer</th>
                            <th>Status</th>
                            <th>Joined Date</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {users.map((user, idx) => (
                            <tr key={user.email + idx}>
                                <td>
                                    <div className="user-info">
                                        <div className="user-avatar" style={{ background: getAvatarColor(user.name) }}>
                                            {getInitials(user.name)}
                                        </div>
                                        <div className="user-details">
                                            <div className="user-name">{user.name}</div>
                                            <div className="user-email">{user.email}</div>
                                        </div>
                                    </div>
                                </td>
                                <td>
                                    <span className={`badge ${user.status === 'ACTIVE' ? 'badge-active' : 'badge-pending'}`}>
                                        {user.status === 'ACTIVE' && <span>✓</span>}
                                        {user.status}
                                    </span>
                                </td>
                                <td>
                                    <span className="text-muted">{user.joined_at}</span>
                                </td>
                                <td>
                                    <button className="view-btn" style={{ opacity: 0.5, cursor: 'not-allowed' }} title="View Details (Coming Soon)">
                                        👁️
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                {users.length === 0 && (
                    <div className="empty-state">
                        <div className="empty-icon">🔍</div>
                        <h3 className="empty-title">
                            {searchQuery ? 'No customer found' : 'Search to view customers'}
                        </h3>
                        <p className="empty-description">
                            {searchQuery ? `We couldn't find a customer with email "${searchQuery}"` : 'Use the search bar above to find a customer by email.'}
                        </p>
                        {searchQuery && (
                            <button onClick={handleClearSearch} className="quick-action-btn">
                                Clear Search
                            </button>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default CustomerList;
